//------------------------------------------------
//
//	ReU: 出現アニメ（画面に入ったら出す）
//	対象は .inview を持つ要素。発火したら .isInview を足すだけで、動きは reu-inview.css が描く。
//
//	発火位置は CSS 変数 --inview-start（画面の上から何%か）。画面幅4段ぶんの値を
//	theme.liquid がメディアクエリ付きで出しているので、ここではその時点の値を読むだけでよい。
//
//	🔑 位置の判定はスクロールのたびに自分で測る。
//	   ブラウザの「枠を狭めて監視する」機能（rootMargin）に発火位置を委ねると、
//	   枠の大きさが後から決まる場所（テーマ編集画面のプレビュー）で設定を無視した動きになる。
//	   監視機能は「画面に入ったかどうか」の絞り込みだけに使い、実際の線越えは毎回測る。
//	   測るのは画面に入っている数個だけなので、スクロール中の負担はごく小さい。
//
//	「まとめて出す」セクション（[data-inview-seq]）は器がひとつの合図になる。
//	器が線を越えたら中の対象へ順番（--inview-i）を配りながらまとめて出す。
//
//	セクション背景の出現（.bgIn）も同じ線で動かす。ただしこちらは一度きりではなく、
//	線を越えたら付ける／線より下へ戻ったら外す、の行ったり来たり（動画の再生に近い）。
//	下へ抜けたあとは付いたまま。見た目は reu-bg-reveal.css が描く。
//
//	[data-inview-range] は「親のセクションが画面にあるあいだだけ」出す（例：背景のロゴ透かし）。
//	親の上端が発火線を越えたら出し、親の下端（＝次のセクションの上端）が画面の中央より上へ来たら消す。
//	こちらも行ったり来たり。見た目は各セクションの CSS が .isShown で描く。
//
//------------------------------------------------
(() => {
    const READY_CLASS = 'inviewReady';
    const DONE_CLASS = 'isInview';
    const SEQ_SELECTOR = '[data-inview-seq]';
    const BG_SELECTOR = '.bgIn';
    const BG_DONE_CLASS = 'isBgIn';
    const BG_FADE_CLASS = 'bgFade';
    const RANGE_SELECTOR = '[data-inview-range]';
    const RANGE_DONE_CLASS = 'isShown';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // JS が生きている時だけ隠す（CSS 側は html.inviewReady にぶら下がっている）
    document.documentElement.classList.add(READY_CLASS);

    let observer = null;
    let waiting = new Set(); // 画面に入っていて、まだ線を越えていないもの
    let bgTargets = []; // 背景セクション。出したあとも測り続ける（上へ戻ったら消すため）
    let rangeTargets = []; // 親のセクションが画面にあるあいだだけ出すもの
    let ticking = false;

    //	発火位置＝画面の上から何%か。0〜100 に収める
    //	🚨 このスクリプトは head の途中で走る＝下にある {% style %} はまだ読めない。
    //	   だから起動時に読まず、必要になったつど読む。
    const startPercent = () => {
        const raw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--inview-start'));
        if (Number.isNaN(raw)) return 60;
        return Math.min(Math.max(raw, 0), 100);
    };

    //	発火線の位置（画面の上から何px）
    const linePx = () => window.innerHeight * (startPercent() / 100);

    //	背景の出現は別の発火位置を持つ（--bg-start・画面幅で分けず1つ）。
    //	値が無い環境では出現アニメと同じ線に落とす
    const bgStartPercent = () => {
        const raw = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--bg-start'));
        if (Number.isNaN(raw)) return startPercent();
        return Math.min(Math.max(raw, 0), 100);
    };
    const bgLinePx = () => window.innerHeight * (bgStartPercent() / 100);

    //	確認線。アドレスに ?inviewline=1 を付けるか、管理画面のチェックを入れると出る
    const hasLineParam = new URLSearchParams(window.location.search).has('inviewline');
    const showDebugLine = () =>
        hasLineParam ||
        getComputedStyle(document.documentElement).getPropertyValue('--inview-debug').trim() === '1';
    const drawLine = (id, color, top, label) => {
        let bar = document.getElementById(id);
        if (!bar) {
            bar = document.createElement('div');
            bar.id = id;
            bar.style.cssText = `position:fixed;left:0;width:100%;height:0;border-top:1px dashed ${color};z-index:9999;pointer-events:none;font:12px/1.4 monospace;color:${color};`;
            document.body.appendChild(bar);
        }
        bar.style.top = `${Math.round(top)}px`;
        bar.textContent = label;
    };
    const drawDebugLine = (line) => {
        if (!showDebugLine()) return;
        drawLine('InviewDebugLine', 'red', line, `出現アニメ ${startPercent()}%（画面の上から${Math.round(line)}px）`);
        if (!bgTargets.length) return;
        const bgLine = bgLinePx();
        drawLine('BgDebugLine', 'blue', bgLine, `背景の出現 ${bgStartPercent()}%（画面の上から${Math.round(bgLine)}px）`);
    };

    //	その要素を出す。まとめて出す器なら、中の対象へ順番を配ってから出す
    const reveal = (element) => {
        if (element.matches(SEQ_SELECTOR)) {
            element.querySelectorAll('.inview').forEach((child, index) => {
                child.style.setProperty('--inview-i', index);
                child.classList.add(DONE_CLASS);
            });
            return;
        }
        element.classList.add(DONE_CLASS);
    };

    //	この要素が発火線を越えるところまでスクロールできるか。
    //	🚨 判定はページの高さが確定してから（readyState complete）だけ。
    //	   読み込み途中は高さが足りず「どれも届かない」と誤判定し、全部その場で出てしまう。
    const unreachable = (element) => {
        if (document.readyState !== 'complete') return false;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const top = element.getBoundingClientRect().top + window.scrollY;
        return top - linePx() > maxScroll;
    };

    //	背景セクションの出し入れ。線を越えていれば出す・線より下なら消す
    const toggleBackground = (element, line) => {
        element.classList.toggle(BG_DONE_CLASS, element.getBoundingClientRect().top <= line);
    };

    //	親の上端が線を越え、親の下端が画面の中央より下にあるあいだだけ出す
    const toggleRange = (element, line) => {
        const rect = (element.parentElement || element).getBoundingClientRect();
        element.classList.toggle(RANGE_DONE_CLASS, rect.top <= line && rect.bottom > window.innerHeight / 2);
    };

    //	画面に入っているものだけを測り、線を越えていれば出す
    const check = () => {
        const line = linePx();
        drawDebugLine(line);
        waiting.forEach((element) => {
            if (element.getBoundingClientRect().top > line && !unreachable(element)) return;
            reveal(element);
            waiting.delete(element);
            if (observer) observer.unobserve(element);
        });
        if (bgTargets.length) {
            const bgLine = bgLinePx();
            bgTargets.forEach((element) => toggleBackground(element, bgLine));
        }
        rangeTargets.forEach((element) => toggleRange(element, line));
    };

    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            check();
        });
    };

    //	観測する対象＝まとめて出す器と、器の外にある .inview
    const targets = () => {
        const seq = Array.from(document.querySelectorAll(SEQ_SELECTOR));
        const single = Array.from(document.querySelectorAll('.inview')).filter(
            (element) => element.closest(SEQ_SELECTOR) === null && !element.classList.contains(DONE_CLASS)
        );
        return seq.concat(single);
    };

    const build = () => {
        //	画面の高さが取れない間は組み立てない（0で計算すると発火位置が画面の下端になる）
        if (!window.innerHeight) return;

        if (observer) observer.disconnect();
        waiting.clear();

        //	ここは「画面に入ったか」の絞り込みだけ。発火位置は check() が毎回測る
        observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    waiting.add(entry.target);
                } else {
                    waiting.delete(entry.target);
                }
            });
            check();
        });

        targets().forEach((element) => observer.observe(element));

        //	背景セクションは「画面に入ったか」で絞り込まず、毎回すべてを測る。
        //	🔑 絞り込むと、線と画面の下端のあいだ（ごく狭い帯）を一気に飛び越えたときに
        //	   消し忘れる。数は多くないので、全部測る方が確実で安い。
        bgTargets = Array.from(document.querySelectorAll(BG_SELECTOR));
        rangeTargets = Array.from(document.querySelectorAll(RANGE_SELECTOR));

        check();

        //	最初の判定が終わってからフェードを許す（読み込み直後にふわっと出るのを防ぐ）
        requestAnimationFrame(() => document.documentElement.classList.add(BG_FADE_CLASS));
    };

    //	確認用の窓口。実機のコンソールで reuInview.state() を叩けば今の状態が分かる
    window.reuInview = {
        state: () => ({
            start: startPercent(),
            line: Math.round(linePx()),
            viewport: window.innerHeight,
            watching: waiting.size,
            waiting: document.querySelectorAll('.inview:not(.isInview)').length,
            done: document.querySelectorAll('.inview.isInview').length,
            bgStart: bgStartPercent(),
            bgLine: Math.round(bgLinePx()),
            bgTotal: bgTargets.length,
            bgOn: document.querySelectorAll('.bgIn.isBgIn').length,
            rangeTotal: rangeTargets.length,
            rangeOn: document.querySelectorAll('[data-inview-range].isShown').length,
        }),
        rebuild: () => build(),
    };

    const init = () => {
        build();
        window.addEventListener('scroll', onScroll, { passive: true });

        //	画像などが載って寸法が確定したあとに、もう一度組み立てる
        window.addEventListener('load', () => build(), { once: true });

        //	画面の大きさが変わったら測り直す。線の位置は check() が毎回読むので、
        //	ここでやるのは対象の拾い直し（幅が変わると4段の値も切り替わる）
        let timer = null;
        const onResize = () => {
            clearTimeout(timer);
            timer = setTimeout(build, 200);
        };
        window.addEventListener('resize', onResize);
        if (window.ResizeObserver) {
            new ResizeObserver(onResize).observe(document.documentElement);
        }

        //	テーマ編集中はセクションを差し替えるたびに拾い直す
        if (window.Shopify && Shopify.designMode) {
            document.addEventListener('shopify:section:load', build);
            document.addEventListener('shopify:section:reorder', build);
        }
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
